import { useCallback, useEffect, useState } from 'react';
import {
  AgxButton,
  AgxDatePicker,
  AgxEmptyState,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import { CoolingOffDetails, CoolingOffType } from '@urbanx/agx.campaigns.client';
import dayjs from 'dayjs';
import LoadingState from 'components/loadingState/LoadingState';
import { useSelectedCampaign } from 'hooks';
import { useBusinessDate } from 'hooks/useBusinessDate';
import './ConditionDatePicker.scss';

interface CoolingOffPeriodPanelProps {
  contractDate?: Date;
  coolingOffDetails: CoolingOffDetails;
  onChange: (updatedCoolingOffDetails: CoolingOffDetails) => void;
  onDelete: () => void;
  locked?: boolean;
}

function getOnChangePayload(businessDaysDiff: number): CoolingOffDetails {
  switch (businessDaysDiff) {
    case 10:
      return {
        coolingOffType: CoolingOffType.TenDays,
        coolingOffDays: undefined,
        noCoolingOffReason: undefined,
      };
    case 5:
      return {
        coolingOffType: CoolingOffType.FiveDays,
        coolingOffDays: undefined,
        noCoolingOffReason: undefined,
      };
  }

  if (businessDaysDiff > 0) {
    return {
      coolingOffType: CoolingOffType.NDays,
      coolingOffDays: businessDaysDiff,
      noCoolingOffReason: undefined,
    };
  }

  return {
    coolingOffType: CoolingOffType.NoCoolingOff,
    coolingOffDays: undefined,
    noCoolingOffReason: undefined,
  };
}

export function CoolingOffPeriodPanel({
  contractDate,
  coolingOffDetails,
  onChange,
  onDelete,
  locked = false,
}: CoolingOffPeriodPanelProps) {
  const { campaign } = useSelectedCampaign();
  const { moveToNextBusinessDay, addBusinessDays, isNonBusinessDay } = useBusinessDate(
    campaign?.state || ''
  );
  const calculateCoolingOffEndDate = useCallback(() => {
    if (!contractDate) return undefined;

    if (coolingOffDetails?.coolingOffType === CoolingOffType.FiveDays) {
      return addBusinessDays(dayjs(contractDate), 5).toDate();
    }
    if (coolingOffDetails?.coolingOffType === CoolingOffType.TenDays) {
      return addBusinessDays(dayjs(contractDate), 10).toDate();
    }
    if (
      coolingOffDetails?.coolingOffType === CoolingOffType.NDays &&
      coolingOffDetails?.coolingOffDays
    ) {
      return addBusinessDays(dayjs(contractDate), coolingOffDetails.coolingOffDays).toDate();
    }
    return undefined;
  }, [coolingOffDetails, contractDate, addBusinessDays]);

  const [coolingOffEndDate, setCoolingOffEndDate] = useState<Date | undefined>(
    calculateCoolingOffEndDate()
  );

  useEffect(() => {
    setCoolingOffEndDate(calculateCoolingOffEndDate());
  }, [calculateCoolingOffEndDate]);

  const countBusinessDaysBetween = useCallback(
    (startDate: Date, endDate: Date): number => {
      let count = 0;
      let current = dayjs(startDate);
      const end = dayjs(endDate);

      while (current.isBefore(end, 'day')) {
        current = current.add(1, 'day');
        if (!isNonBusinessDay(current)) {
          count++;
        }
      }
      return count;
    },
    [isNonBusinessDay]
  );

  const onCoolingOffEndDateChanged = useCallback(
    (value: Date | null) => {
      const endDate = value ? moveToNextBusinessDay(dayjs(value)).toDate() : undefined;

      setCoolingOffEndDate(endDate);

      if (!endDate || !contractDate) return;

      const businessDaysDiff = countBusinessDaysBetween(contractDate, endDate);
      onChange(getOnChangePayload(businessDaysDiff));
    },
    [contractDate, countBusinessDaysBetween, moveToNextBusinessDay, onChange]
  );

  if (!coolingOffDetails) {
    return <LoadingState />;
  }

  return (
    <SubPagePanel
      key="cooling-off-period-panel"
      expandable
      panelTitle="Cooling Off Period"
      defaultExpanded
    >
      <AgxSpacer
        gap="spacing-xl"
        fill
        contentJustification={
          coolingOffDetails?.coolingOffType === CoolingOffType.NoCoolingOff ? 'center' : undefined
        }
        contentAlignment={
          coolingOffDetails?.coolingOffType !== CoolingOffType.NoCoolingOff ? 'start' : undefined
        }
        padding={{ top: 'spacing-3xl', bottom: 'spacing-3xl' }}
      >
        {coolingOffDetails?.coolingOffType === CoolingOffType.NoCoolingOff ? (
          <AgxEmptyState variant="minimal" label="There is no Cooling Off" />
        ) : (
          <AgxDatePicker
            id="cooling-off-period-due-date"
            className="contract-information-date-picker"
            label="Due Date"
            value={
              coolingOffEndDate
                ? moveToNextBusinessDay(dayjs(coolingOffEndDate)).toDate()
                : undefined
            }
            optionalText=""
            disabled={locked}
            minDate={contractDate ? dayjs(contractDate).toDate() : undefined}
            onChange={onCoolingOffEndDateChanged}
          />
        )}
        {locked && (
          <AgxTypography
            id="cooling-off-period-completed-task-message"
            type="body_ui"
            size="small"
            color="var(--text-secondary)"
          >
            Cooling off cannot be changed because the Cooling Off task is complete.
          </AgxTypography>
        )}
        <AgxButton
          id="cooling-off-period-delete"
          variant="link"
          danger
          text="Delete"
          disabled={locked}
          onClick={onDelete}
        />
      </AgxSpacer>
    </SubPagePanel>
  );
}
